import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box, Typography, Alert, LinearProgress, Paper,
  TextField, Button, Chip, Stack, IconButton, Tooltip, Skeleton,
  Dialog, DialogTitle, DialogContent, DialogActions,
} from '@mui/material';
import LockRoundedIcon from '@mui/icons-material/LockRounded';
import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded';
import SlideshowRoundedIcon from '@mui/icons-material/SlideshowRounded';
import UploadFileRoundedIcon from '@mui/icons-material/UploadFileRounded';
import { useTheme, alpha } from '@mui/material/styles';
import { useAdminScreen } from '../admin/useAdminScreen';
import {
  SeatsTab, CreditsTab, BillingTab, DeployAddInCard, AuditLogTab,
  CoreOrgLoadErrorState, isCoreOrgLoadBlocked,
} from '../admin/AdminScreen';
import { consoleCardSx } from '../admin/consoleChips';
import { usePodMembership, useOrgAccess } from '../../shared/contexts';
import { useAuth } from '../../shared/contexts/UserContext';
import { buildFunctionUrl } from '../../shared/utils';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { db, storage } from '../../shared/services/firebase';
import { ref as storageRef, uploadBytesResumable } from 'firebase/storage';

// Field-label style (label-above-input), the spec's 13px / 500 treatment.
const fieldLabelSx = { fontSize: 13, fontWeight: 500, color: 'text.secondary', mb: 0.5, display: 'block' };

// Quiet relative-time label for the "Updated …" freshness cue. Static until the
// next refresh/action re-renders, like the screenshots' "Last updated 15m ago".
function relativeTime(ts) {
  if (!ts) return '';
  const sec = Math.max(0, (Date.now() - ts) / 1000);
  if (sec < 60) return 'just now';
  const min = Math.round(sec / 60);
  if (min < 60) return `${min}m ago`;
  return `${Math.round(min / 60)}h ago`;
}

// Project details (name + project code on receipts) live under Billing now —
// the standalone Project section is gone for org users (one roster, one door).
// Saves through the existing updatePodMetadata endpoint (project-admin gated).
function ProjectDetailsCard() {
  const theme = useTheme();
  const { currentUser } = useAuth();
  const { pod, podRole } = usePodMembership();
  const [name, setName] = useState('');
  const [code, setCode] = useState('');
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState(null);

  useEffect(() => {
    if (pod?.name) setName(pod.name);
    if (pod?.projectCode) setCode(pod.projectCode);
  }, [pod?.name, pod?.projectCode]);

  const save = useCallback(async () => {
    setSaving(true);
    setMsg(null);
    try {
      const idToken = await currentUser.getIdToken();
      const res = await fetch(buildFunctionUrl('updatePodMetadata'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${idToken}` },
        body: JSON.stringify({ name: name.trim(), projectCode: code.trim() }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.error || 'Could not save project details');
      setMsg({ severity: 'success', text: 'Project details saved.' });
    } catch (e) {
      setMsg({ severity: 'error', text: e.message });
    } finally {
      setSaving(false);
    }
  }, [currentUser, name, code]);

  // Practice orgs have no project yet; nothing to edit. Project settings are
  // writable by the project admin (the buyer).
  if (!pod || podRole !== 'admin') return null;

  const dirty = name.trim() !== (pod?.name || '') || code.trim() !== (pod?.projectCode || '');

  // Label-above-input fields (the reference console form pattern), in a
  // responsive two-column grid, with the save action gated on a real change.

  return (
    <Paper elevation={0} sx={{ ...consoleCardSx(theme), mt: 2, mb: 2 }}>
      <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.25 }}>Project details</Typography>
      <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
        The name appears in invites; the code prints on your receipts for client billing.
      </Typography>
      {msg && <Alert severity={msg.severity} sx={{ mb: 2 }} onClose={() => setMsg(null)}>{msg.text}</Alert>}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
        <Box>
          <Typography component="label" htmlFor="project-name" sx={fieldLabelSx}>Project name</Typography>
          <TextField id="project-name" size="small" fullWidth value={name} placeholder="Acme Capital"
            onChange={(e) => setName(e.target.value)} disabled={saving} />
        </Box>
        <Box>
          <Typography component="label" htmlFor="project-code" sx={fieldLabelSx}>Project code</Typography>
          <TextField id="project-code" size="small" fullWidth value={code} placeholder="e.g. PROJECT-ATLAS"
            onChange={(e) => setCode(e.target.value)} disabled={saving} />
        </Box>
      </Box>
      <Box sx={{ mt: 2 }}>
        <Button variant="contained" disableElevation onClick={save} disabled={saving || !dirty || !name.trim()}>
          {saving ? 'Saving…' : 'Save changes'}
        </Button>
      </Box>
    </Paper>
  );
}

// Firm template: the org's default Office template file. Members see it as the
// "Firm template" option in the add-in's Workflows > Templates dropdown
// (delivered through getFirmStyleGuide -> { name, fileName, downloadUrl }).
// Upload transport: the file is staged to the caller's OWN owner-scoped
// storage path (users/{uid}/firmTemplateStaging/, the one client-writable
// prefix), then setFirmTemplate validates and copies it into the backend-only
// org path. Org admins only; the endpoints enforce the role server-side.
const FIRM_TEMPLATE_ACCEPT = '.pptx,.potx,.xlsx,.xltx,.docx,.dotx';
const FIRM_TEMPLATE_MAX_BYTES = 30 * 1024 * 1024;

function FirmTemplateCard() {
  const theme = useTheme();
  const { currentUser } = useAuth();
  const { isOrgAdmin } = useOrgAccess();
  const [template, setTemplate] = useState(null);
  const [loaded, setLoaded] = useState(false);
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState(null);
  const [msg, setMsg] = useState(null);
  const [loadFailed, setLoadFailed] = useState(false);
  const [loadAttempt, setLoadAttempt] = useState(0);
  const [confirmRemove, setConfirmRemove] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoadFailed(false);
        const idToken = await currentUser.getIdToken();
        const res = await fetch(buildFunctionUrl('getFirmStyleGuide'), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${idToken}` },
          body: JSON.stringify({}),
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(data?.error || 'Could not load the firm template');
        if (!cancelled) {
          setTemplate(data?.firmTemplate?.downloadUrl ? data.firmTemplate : null);
          setLoaded(true);
        }
      } catch (e) {
        if (!cancelled) {
          setLoadFailed(true);
          setLoaded(true);
          setMsg({ severity: 'error', text: e.message || 'Could not load the firm template' });
        }
      }
    })();
    return () => { cancelled = true; };
  }, [currentUser, loadAttempt]);

  const callEndpoint = useCallback(async (name, body) => {
    const idToken = await currentUser.getIdToken();
    const res = await fetch(buildFunctionUrl(name), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${idToken}` },
      body: JSON.stringify(body || {}),
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(data?.error || `${name} failed`);
    return data;
  }, [currentUser]);

  const handleFile = useCallback(async (file) => {
    if (!file || !currentUser?.uid || !storage) return;
    setMsg(null);
    const ext = (file.name.includes('.') ? file.name.split('.').pop() : '').toLowerCase();
    if (!['pptx', 'potx', 'xlsx', 'xltx', 'docx', 'dotx'].includes(ext)) {
      setMsg({ severity: 'error', text: 'Choose a PowerPoint, Excel, or Word file.' });
      return;
    }
    if (file.size > FIRM_TEMPLATE_MAX_BYTES) {
      setMsg({ severity: 'error', text: 'Templates can be at most 30MB.' });
      return;
    }
    setBusy(true);
    setProgress(0);
    try {
      // Stage under the caller's own storage path (the client-writable prefix),
      // then hand off to the server, which validates and copies into the org.
      const stagingPath = `users/${currentUser.uid}/firmTemplateStaging/${Date.now()}-${file.name}`;
      const task = uploadBytesResumable(storageRef(storage, stagingPath), file);
      await new Promise((resolve, reject) => {
        task.on(
          'state_changed',
          (snap) => setProgress(snap.totalBytes ? (snap.bytesTransferred / snap.totalBytes) * 100 : 0),
          reject,
          resolve
        );
      });
      setProgress(null);
      const data = await callEndpoint('setFirmTemplate', { stagingPath, fileName: file.name });
      setTemplate(data?.firmTemplate || null);
      setMsg({ severity: 'success', text: 'Template saved. Members see it as "Firm default" under Templates.' });
    } catch (e) {
      setMsg({ severity: 'error', text: e.message || 'Upload failed' });
    } finally {
      setBusy(false);
      setProgress(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  }, [currentUser, callEndpoint]);

  const handleRemove = useCallback(async () => {
    setConfirmRemove(false);
    setBusy(true);
    setMsg(null);
    try {
      await callEndpoint('removeFirmTemplate', {});
      setTemplate(null);
      setMsg({ severity: 'success', text: 'Template removed.' });
    } catch (e) {
      setMsg({ severity: 'error', text: e.message || 'Could not remove the template' });
    } finally {
      setBusy(false);
    }
  }, [callEndpoint]);

  const uploadedLabel = template?.uploadedAtIso
    ? new Date(template.uploadedAtIso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
    : null;

  return (
    <Paper elevation={0} sx={{ ...consoleCardSx(theme), mb: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.25 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>Firm template</Typography>
        {loaded && !isOrgAdmin && (
          <Chip
            size="small"
            variant="outlined"
            icon={<LockRoundedIcon sx={{ fontSize: 13 }} />}
            label="Read only"
            sx={{ height: 20, fontSize: 11, color: 'text.secondary', '& .MuiChip-label': { px: 0.75 }, '& .MuiChip-icon': { ml: 0.5, color: 'text.secondary' } }}
          />
        )}
      </Box>
      <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
        Upload a PowerPoint, Excel, or Word file for your team. It appears as the firm template
        in the matching Office app and remains optional for members. Maximum file size: 30MB.
      </Typography>
      {msg && <Alert severity={msg.severity} sx={{ mb: 2 }} onClose={() => setMsg(null)}>{msg.text}</Alert>}

      {loadFailed ? (
        <Button
          size="small"
          variant="outlined"
          onClick={() => {
            setLoaded(false);
            setMsg(null);
            setLoadAttempt((value) => value + 1);
          }}
        >
          Retry loading template
        </Button>
      ) : !loaded ? (
        // Skeleton mirrors the loaded row's shape so the card doesn't jump.
        <Stack direction="row" spacing={1.5} alignItems="center" sx={{ py: 0.5 }}>
          <Skeleton variant="rounded" width={36} height={36} sx={{ borderRadius: '8px' }} />
          <Box sx={{ flex: 1 }}>
            <Skeleton variant="text" width={180} />
            <Skeleton variant="text" width={120} sx={{ fontSize: 12 }} />
          </Box>
        </Stack>
      ) : template ? (
        <Stack direction="row" spacing={1.5} alignItems="center" sx={{ py: 0.5 }}>
          <Box
            sx={{
              width: 36, height: 36, borderRadius: '8px', flexShrink: 0,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              backgroundColor: alpha(theme.palette.primary.main, 0.1),
            }}
          >
            <SlideshowRoundedIcon sx={{ fontSize: 18, color: 'primary.main' }} />
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="body2" noWrap sx={{ fontWeight: 500 }}>{template.name}</Typography>
            <Typography variant="caption" noWrap sx={{ color: 'text.secondary', display: 'block' }}>
              {template.fileName}{uploadedLabel ? ` · uploaded ${uploadedLabel}` : ''}
            </Typography>
          </Box>
          {isOrgAdmin && (
            <Stack direction="row" spacing={1} sx={{ flexShrink: 0 }}>
              <Button size="small" variant="outlined" disabled={busy} onClick={() => fileInputRef.current?.click()} sx={{ minHeight: 32, borderRadius: '8px' }}>
                Replace
              </Button>
              <Button size="small" variant="text" color="error" disabled={busy} onClick={() => setConfirmRemove(true)} sx={{ minHeight: 32, borderRadius: '8px' }}>
                Remove
              </Button>
            </Stack>
          )}
        </Stack>
      ) : (
        <Stack direction="row" spacing={1.5} alignItems="center" sx={{ py: 0.5 }}>
          <Box
            sx={{
              width: 36, height: 36, borderRadius: '8px', flexShrink: 0,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              backgroundColor: alpha(theme.palette.text.primary, 0.04),
            }}
          >
            <UploadFileRoundedIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
          </Box>
          <Typography variant="body2" sx={{ color: 'text.secondary', flex: 1 }}>
            No template yet{isOrgAdmin ? ' - upload your firm deck to pin it for everyone.' : '.'}
          </Typography>
          {isOrgAdmin && (
            <Button size="small" variant="contained" disableElevation disabled={busy} onClick={() => fileInputRef.current?.click()} sx={{ minHeight: 32, borderRadius: '8px' }}>
              Upload template
            </Button>
          )}
        </Stack>
      )}

      {progress != null && (
        <LinearProgress
          variant="determinate"
          value={progress}
          sx={{ mt: 1.5, height: 6, borderRadius: 3, backgroundColor: alpha(theme.palette.primary.main, 0.1) }}
        />
      )}
      {busy && progress == null && <LinearProgress sx={{ mt: 1.5, height: 6, borderRadius: 3 }} />}

      <input
        ref={fileInputRef}
        type="file"
        accept={FIRM_TEMPLATE_ACCEPT}
        style={{ display: 'none' }}
        onChange={(e) => handleFile(e.target.files?.[0])}
        aria-label="Upload firm template"
      />

      <Dialog open={confirmRemove} onClose={busy ? undefined : () => setConfirmRemove(false)} fullWidth maxWidth="xs">
        <DialogTitle sx={{ fontSize: 17, fontWeight: 600 }}>Remove the firm template?</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            The "Firm default" entry disappears from everyone's Templates menu and the stored
            file is deleted. You can upload a new template any time.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setConfirmRemove(false)} disabled={busy}>Cancel</Button>
          <Button variant="contained" color="error" disableElevation onClick={handleRemove} disabled={busy}>
            Remove template
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
}

// Firm style guide: per-org always-on instructions injected into every deck.
// Read by any member (so people can see what is applied); editable by org
// admins only. The backend enforces orgAdmin on write, so a billing admin sees
// it read-only here.
function FirmStyleGuideCard() {
  const theme = useTheme();
  const { currentUser } = useAuth();
  const { isOrgAdmin } = useOrgAccess();
  const [guide, setGuide] = useState('');
  const [original, setOriginal] = useState('');
  const [loaded, setLoaded] = useState(false);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState(null);
  const [loadFailed, setLoadFailed] = useState(false);
  const [loadAttempt, setLoadAttempt] = useState(0);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoadFailed(false);
        const idToken = await currentUser.getIdToken();
        const res = await fetch(buildFunctionUrl('getFirmStyleGuide'), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${idToken}` },
          body: JSON.stringify({}),
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(data?.error || 'Could not load the firm style guide');
        if (!cancelled) {
          const g = typeof data?.guide === 'string' ? data.guide : '';
          setGuide(g);
          setOriginal(g);
          setLoaded(true);
        }
      } catch (e) {
        if (!cancelled) {
          setLoadFailed(true);
          setLoaded(true);
          setMsg({ severity: 'error', text: e.message || 'Could not load the firm style guide' });
        }
      }
    })();
    return () => { cancelled = true; };
  }, [currentUser, loadAttempt]);

  const save = useCallback(async () => {
    setSaving(true);
    setMsg(null);
    try {
      const idToken = await currentUser.getIdToken();
      const res = await fetch(buildFunctionUrl('setFirmStyleGuide'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${idToken}` },
        body: JSON.stringify({ guide }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.error || 'Could not save the firm style guide');
      setOriginal(guide);
      setMsg({ severity: 'success', text: 'Saved. It applies to every deck from the next message.' });
    } catch (e) {
      setMsg({ severity: 'error', text: e.message });
    } finally {
      setSaving(false);
    }
  }, [currentUser, guide]);

  const dirty = guide !== original;

  return (
    <Paper elevation={0} sx={{ ...consoleCardSx(theme), mb: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.25 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>Firm style guide</Typography>
        {loaded && !isOrgAdmin && (
          <Chip
            size="small"
            variant="outlined"
            icon={<LockRoundedIcon sx={{ fontSize: 13 }} />}
            label="Read only"
            sx={{ height: 20, fontSize: 11, color: 'text.secondary', '& .MuiChip-label': { px: 0.75 }, '& .MuiChip-icon': { ml: 0.5, color: 'text.secondary' } }}
          />
        )}
      </Box>
      <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
        Always-on instructions Restack applies to every deck for everyone in your firm: palette, fonts,
        title conventions, and house rules. {isOrgAdmin ? 'Edit it below.' : 'Only firm admins can edit this.'}
      </Typography>
      {msg && <Alert severity={msg.severity} sx={{ mb: 2 }} onClose={() => setMsg(null)}>{msg.text}</Alert>}
      {loadFailed && (
        <Button
          size="small"
          variant="outlined"
          sx={{ mb: 2 }}
          onClick={() => {
            setLoaded(false);
            setMsg(null);
            setLoadAttempt((value) => value + 1);
          }}
        >
          Retry loading guide
        </Button>
      )}
      <Typography component="label" htmlFor="firm-style-instructions" sx={fieldLabelSx}>Instructions</Typography>
      <TextField
        id="firm-style-instructions"
        size="small"
        fullWidth
        multiline
        minRows={6}
        maxRows={20}
        value={guide}
        placeholder="e.g. Use our navy #0B1F3A and warm grey palette. Action titles must be complete sentences. Never pair red and green."
        onChange={(e) => setGuide(e.target.value.slice(0, 8000))}
        disabled={saving || !loaded || loadFailed || !isOrgAdmin}
        sx={{ '& textarea': { resize: 'vertical', maxHeight: 420, overflow: 'auto' } }}
      />
      <Typography variant="caption" sx={{ color: guide.length > 7000 ? 'warning.main' : 'text.secondary', display: 'block', mt: 0.5 }}>
        {guide.length.toLocaleString()} / 8,000 characters{guide.length >= 8000 ? ' (limit reached)' : ''}
      </Typography>
      {isOrgAdmin && (
        <Box sx={{ mt: 2 }}>
          <Button variant="contained" disableElevation onClick={save} disabled={saving || loadFailed || !dirty}>
            {saving ? 'Saving…' : 'Save changes'}
          </Button>
        </Box>
      )}
    </Paper>
  );
}

// Personal style guide: the signed-in user's own always-on instructions,
// stored at users/{uid}/workspace/styleGuide (owner-writable per
// firestore.rules) and injected on every turn for this user only, after the
// firm guide. Same editor is available inside the add-in sidebar.
function PersonalStyleGuideCard() {
  const theme = useTheme();
  const { currentUser } = useAuth();
  const [guide, setGuide] = useState('');
  const [original, setOriginal] = useState('');
  const [loaded, setLoaded] = useState(false);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        if (currentUser?.uid && db) {
          const snap = await getDoc(doc(db, 'users', currentUser.uid, 'workspace', 'styleGuide'));
          const text = snap.exists() ? String(snap.data()?.guide || '') : '';
          if (!cancelled) { setGuide(text); setOriginal(text); }
        }
      } catch (e) {
        if (!cancelled) setMsg({ severity: 'error', text: e.message });
      } finally {
        if (!cancelled) setLoaded(true);
      }
    })();
    return () => { cancelled = true; };
  }, [currentUser?.uid]);

  const save = useCallback(async () => {
    if (!currentUser?.uid || !db) return;
    setSaving(true);
    setMsg(null);
    try {
      const text = guide.trim().slice(0, 8000);
      await setDoc(doc(db, 'users', currentUser.uid, 'workspace', 'styleGuide'), {
        guide: text,
        updatedAt: new Date().toISOString(),
      }, { merge: true });
      setGuide(text);
      setOriginal(text);
      setMsg({ severity: 'success', text: 'Saved. Applies from your next message.' });
    } catch (e) {
      setMsg({ severity: 'error', text: e.message });
    } finally {
      setSaving(false);
    }
  }, [currentUser?.uid, guide]);

  const dirty = guide !== original;

  return (
    <Paper elevation={0} sx={{ ...consoleCardSx(theme), mb: 2 }}>
      <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.25 }}>Your style</Typography>
      <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
        Personal preferences applied to everything you make, on top of the firm guide. Only you see and edit these.
      </Typography>
      {msg && <Alert severity={msg.severity} sx={{ mb: 2 }} onClose={() => setMsg(null)}>{msg.text}</Alert>}
      <Typography component="label" htmlFor="personal-style-instructions" sx={fieldLabelSx}>Instructions</Typography>
      <TextField
        id="personal-style-instructions"
        size="small"
        fullWidth
        multiline
        minRows={4}
        maxRows={16}
        value={guide}
        placeholder="e.g. I prefer dense, data-heavy slides. Default charts to quarterly granularity. Sign off memos as HS."
        onChange={(e) => setGuide(e.target.value.slice(0, 8000))}
        disabled={saving || !loaded}
        sx={{ '& textarea': { resize: 'vertical', maxHeight: 360, overflow: 'auto' } }}
      />
      <Typography variant="caption" sx={{ color: guide.length > 7000 ? 'warning.main' : 'text.secondary', display: 'block', mt: 0.5 }}>
        {guide.length.toLocaleString()} / 8,000 characters{guide.length >= 8000 ? ' (limit reached)' : ''}
      </Typography>
      <Box sx={{ mt: 2 }}>
        <Button variant="contained" disableElevation onClick={save} disabled={saving || !dirty}>
          {saving ? 'Saving…' : 'Save changes'}
        </Button>
      </Box>
    </Paper>
  );
}

// One flat org-admin section (Members / Credits / Billing), rendered top-level
// in the console now — no "Organization" wrapper, no sub-nav. All data comes
// from the role-gated endpoints via useAdminScreen (never a Firestore org
// read). A non-admin never routes here (the rail hides these for them); the
// guard below is a defensive fallback. `section` is the active flat route.
export default function OrgAdminSection({ section }) {
  const s = useAdminScreen();
  const theme = useTheme();

  if (s.accessLoading || (s.loading && s.canAccessAdmin && !s.org)) {
    // Skeleton shaped like the loaded sections (stat row + one table/card
    // surface) so the pane doesn't jump when data lands.
    return (
      <Box>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ mb: 2 }}>
          {[0, 1].map((i) => (
            <Paper key={i} elevation={0} sx={{ ...consoleCardSx(theme), flex: 1, minWidth: 160 }}>
              <Skeleton variant="text" width={90} sx={{ fontSize: 11 }} />
              <Skeleton variant="text" width={120} sx={{ fontSize: '1.75rem' }} />
              <Skeleton variant="text" width={70} sx={{ fontSize: 12 }} />
            </Paper>
          ))}
        </Stack>
        <Paper elevation={0} sx={consoleCardSx(theme)}>
          {[0, 1, 2].map((i) => (
            <Stack key={i} direction="row" spacing={2} alignItems="center" sx={{ py: 1.25 }}>
              <Skeleton variant="text" width="30%" />
              <Skeleton variant="rounded" width={84} height={22} sx={{ borderRadius: '6px' }} />
              <Box sx={{ flex: 1 }} />
              <Skeleton variant="text" width={56} />
            </Stack>
          ))}
        </Paper>
      </Box>
    );
  }

  // Defensive fallback: a non-admin who reaches an admin route directly.
  if (!s.canAccessAdmin) {
    return (
      <Box>
        <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
          Your seat is part of your firm's Restack plan. Seats, usage, and billing are managed by your
          organization's admins. If you need anything changed, ask your admin.
        </Typography>
        <DeployAddInCard />
      </Box>
    );
  }

  if (isCoreOrgLoadBlocked(s)) {
    return <CoreOrgLoadErrorState s={s} />;
  }

  return (
    <Box>
      {s.error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => s.setError(null)}>{s.error}</Alert>}
      {s.actionMsg && <Alert severity={s.actionSeverity || 'success'} sx={{ mb: 2 }}>{s.actionMsg}</Alert>}
      {s.busy && <LinearProgress sx={{ mb: 2, borderRadius: 1 }} />}

      {/* Quiet freshness cue + manual refresh (the screenshots' "Last updated"
          cluster), on the live admin surface. Firmstyle has its own fetch, so
          skip it there. */}
      {section !== 'firmstyle' && (
        <Stack direction="row" alignItems="center" justifyContent="flex-end" spacing={0.25} sx={{ mb: 1.5 }}>
          {(section === 'activity' ? s.auditLoadedAt : s.loadedAt) && (
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              Updated {relativeTime(section === 'activity' ? s.auditLoadedAt : s.loadedAt)}
            </Typography>
          )}
          <Tooltip title="Refresh">
            <span>
              <IconButton
                size="small"
                onClick={() => section === 'activity' ? s.loadAuditLog({ reset: true }) : s.refresh()}
                disabled={section === 'activity' ? s.auditLoading : (s.loading || s.busy)}
                aria-label={section === 'activity' ? 'Refresh activity' : 'Refresh organization data'}
              >
                <RefreshRoundedIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
              </IconButton>
            </span>
          </Tooltip>
        </Stack>
      )}

      {section === 'members' && <SeatsTab s={s} />}
      {section === 'credits' && <CreditsTab s={s} />}
      {section === 'activity' && <AuditLogTab s={s} />}
      {section === 'firmstyle' && (
        <>
          <FirmTemplateCard />
          <FirmStyleGuideCard />
          <PersonalStyleGuideCard />
        </>
      )}
      {section === 'billing' && (
        <>
          <BillingTab s={s} />
          <ProjectDetailsCard />
        </>
      )}
    </Box>
  );
}
